// InstallationDetails.js
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import axios from "axios";
import config from "../../config.js";
import { mapStyles } from "../dashboard/functions.js";
// --- Google Maps imports ---
import {
  GoogleMap,
  useLoadScript,
  Marker,
  OverlayView,
} from "@react-google-maps/api";

const GOOGLE_MAPS_LIBRARIES = ["places"];
const DEFAULT_MAP_CENTER = { lat: 12.9716, lng: 77.5946 }; // Bengaluru fallback
const mapContainerStyle = {
  width: "100%",
  height: "100%",
};

const getVehicleSiteId = (vehicle) =>
  vehicle?.siteId || vehicle?.site?.siteId || vehicle?.site?.id || "";

const getSiteOptionValue = (site) => site?.siteId || site?.id || "";

const getSiteDisplayName = (site) =>
  site?.name || site?.siteName || site?.siteId || site?.id || "";

const getSiteIdentityValues = (site) =>
  [site?.id, site?.siteId]
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map((value) => String(value));

const mergeById = (...lists) => {
  const map = new Map();
  lists.flat().forEach((item) => {
    if (!item?.id) return;
    map.set(String(item.id), item);
  });
  return Array.from(map.values());
};

export default function InstallationDetails({
  formData,
  handleInputChange,
  RequiredLabel,
  StandardLabel,
  errors = {},
  t,
  selectedOrganization,
  setSelectedOrganization,
  isEditMode,
  disabled = false,
  lockOrganization = false,
}) {
  const [organizations, setOrganizations] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [stops, setStops] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [cities, setCities] = useState([]);
  const previousOrganizationIdRef = useRef(selectedOrganization?.id || null);

  // --- Google Map instance ref ---
  const mapRef = useRef(null);

  const { isLoaded: isMapLoaded } = useLoadScript({
    googleMapsApiKey: config.googleMapsApiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const getNormalizedVehicle = (vehicle) =>
    vehicle
      ? {
          ...vehicle,
          registrationNumber:
            vehicle.registrationNumber || vehicle.VehicleNumber || "",
          model: vehicle.model || vehicle.Model || "",
          VehicleNumber:
            vehicle.VehicleNumber || vehicle.registrationNumber || "",
          Model: vehicle.Model || vehicle.model || "",
        }
      : null;

  const makeVehicleFormValue = (vehicle) =>
    vehicle
      ? {
          id: vehicle.id,
          name: vehicle.name,
          registrationNumber:
            vehicle.registrationNumber || vehicle.VehicleNumber || "",
          model: vehicle.model || vehicle.Model || "",
          VehicleNumber:
            vehicle.VehicleNumber || vehicle.registrationNumber || "",
          Model: vehicle.Model || vehicle.model || "",
          siteId: vehicle.siteId || vehicle.site?.siteId || vehicle.site?.id,
          site: vehicle.site || null,
        }
      : null;

  const updateVehicleFields = (vehicle) => {
    if (!vehicle) return; // ✅ guard
    const vehicleData = makeVehicleFormValue(vehicle);
    handleInputChange({ target: { value: vehicleData } }, "vehicle");
    handleInputChange(
      { target: { value: vehicleData?.registrationNumber || "" } },
      "vehicleRegistrationNumber",
    );
    handleInputChange(
      { target: { value: vehicleData?.model || "" } },
      "vehicleModel",
    );
  };

  const clearVehicleFields = () => {
    handleInputChange({ target: { value: null } }, "vehicle");
    handleInputChange({ target: { value: "" } }, "vehicleRegistrationNumber");
    handleInputChange({ target: { value: "" } }, "vehicleModel");
  };

  const clearStopFields = () => {
    handleInputChange({ target: { value: null } }, "stop");
    handleInputChange({ target: { value: "" } }, "stopId");
    handleInputChange({ target: { value: "" } }, "stopName");
  };

  const shouldKeepEditVehicle = () =>
    isEditMode &&
    (formData.assignmentType || "VEHICLE") === "VEHICLE" &&
    formData.vehicle?.id;

  const selectedSite = useMemo(
    () =>
      sites.find(
        (site) =>
          String(site.siteId) === String(formData.site) ||
          String(site.id) === String(formData.site),
      ),
    [sites, formData.site],
  );

  const selectedSiteValues = useMemo(
    () => new Set(getSiteIdentityValues(selectedSite)),
    [selectedSite],
  );

  // Site dropdown values use Site.siteId when available, while vehicles store
  // the numeric Site.id. Compare both identifiers and include nested site
  // vehicles from /site/organization so the dropdown mirrors that response.
  const siteFilteredVehicles = useMemo(() => {
    const selectedVehicle = getNormalizedVehicle(formData.vehicle);
    const selectedVehicleId = selectedVehicle?.id;
    const nestedVehicles = Array.isArray(selectedSite?.vehicles)
      ? selectedSite.vehicles
      : [];
    const sourceVehicles = mergeById(vehicles, nestedVehicles);

    const filteredVehicles =
      formData.site && selectedSiteValues.size > 0
        ? sourceVehicles.filter((vehicle) => {
            const vehicleSiteValues = [
              getVehicleSiteId(vehicle),
              vehicle?.site?.id,
              vehicle?.site?.siteId,
              vehicle?.siteId,
            ]
              .filter(
                (value) =>
                  value !== null && value !== undefined && value !== "",
              )
              .map((value) => String(value));

            return vehicleSiteValues.some((value) =>
              selectedSiteValues.has(value),
            );
          })
        : sourceVehicles;

    if (
      selectedVehicle &&
      !filteredVehicles.some(
        (vehicle) => String(vehicle.id) === String(selectedVehicleId),
      )
    ) {
      return [...filteredVehicles, selectedVehicle];
    }

    return filteredVehicles;
  }, [vehicles, selectedSite, selectedSiteValues, formData.site, formData.vehicle]);

  const selectedVehicleName = useMemo(() => {
    if (!formData.vehicle?.id) {
      return "";
    }
    const inList = siteFilteredVehicles.some(
      (v) => String(v.id) === String(formData.vehicle?.id),
    );
    if (inList) return formData.vehicle?.name || "";
    if (isEditMode) return formData.vehicle?.name || "";
    return "";
  }, [siteFilteredVehicles, formData.vehicle, isEditMode]);

  const siteFilteredStops = useMemo(() => {
    if (!formData.site || !selectedSite) {
      return mergeById(
        stops,
        selectedSite?.stops || [],
        selectedSite?.Stops || [],
        selectedSite?.gates || [],
      );
    }

    const sourceStops = mergeById(
      stops,
      selectedSite?.stops || [],
      selectedSite?.Stops || [],
      selectedSite?.gates || [],
    );

    const filteredStops = sourceStops.filter((stop) => {
      const stopSiteValues = [stop?.siteId, stop?.site?.id, stop?.site?.siteId]
        .filter(
          (value) => value !== null && value !== undefined && value !== "",
        )
        .map((value) => String(value));

      return stopSiteValues.some((value) => selectedSiteValues.has(value));
    });

    if (
      formData.stop?.id &&
      !filteredStops.some((stop) => String(stop.id) === String(formData.stop?.id))
    ) {
      return [...filteredStops, formData.stop];
    }

    return filteredStops;
  }, [stops, selectedSite, selectedSiteValues, formData.site, formData.stop]);

  useEffect(() => {
    fetchOrganization();
  }, []);

  const clearOrganizationDependentFields = ({ clearCity = false } = {}) => {
    setVehicles([]);
    setStops([]);
    setSites([]);

    if (clearCity) {
      handleInputChange({ target: { value: "" } }, "city");
    }

    handleInputChange({ target: { value: "" } }, "site");
    clearVehicleFields();
    clearStopFields();
  };

  // Fetch organization-specific data when organization changes
  useEffect(() => {
    const previousOrganizationId = previousOrganizationIdRef.current;
    const currentOrganizationId = selectedOrganization?.id || null;
    const organizationChanged =
      previousOrganizationId !== null &&
      currentOrganizationId !== previousOrganizationId;

    previousOrganizationIdRef.current = currentOrganizationId;

    if (selectedOrganization?.id) {
      // ✅ ONLY clear if org changed AND not in edit mode
      if (organizationChanged && !isEditMode) {
        clearOrganizationDependentFields();
      }

      fetchOrganizationVehicles(selectedOrganization.id);
      fetchOrganizationStops(selectedOrganization.id);
      fetchSites(selectedOrganization.id);
      handleInputChange(
        { target: { value: selectedOrganization.id } },
        "organizationId",
      );

      if (selectedOrganization.city) {
        const cityEvent = { target: { value: selectedOrganization.city } };
        handleInputChange(cityEvent, "city");
      }
    } else {
      handleInputChange({ target: { value: "" } }, "organizationId");
      clearOrganizationDependentFields({ clearCity: true });
    }
  }, [selectedOrganization?.id]); // ✅ primitive dep only

  const fetchOrganizationVehicles = async (organizationId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${config.baseURL}/vehicle/organization/independent/${organizationId}`,
        { withCredentials: true },
      );
      const vehicleData = response?.data?.vehicles || response?.data || [];
      setVehicles(vehicleData);

      if (vehicleData.length === 1) {
        const singleVehicle = vehicleData[0];
        updateVehicleFields(singleVehicle);
      }
    } catch (error) {
      console.log("Error fetching organization vehicles:", error);
      setVehicles([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizationStops = async (organizationId) => {
    try {
      const response = await axios.get(`${config.baseURL}/stop/organization/independent/${organizationId}`, {
        withCredentials: true,
      });
      const stopData = Array.isArray(response.data)
        ? response.data
        : Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data?.stops)
            ? response.data.stops
            : [];
      setStops(stopData);
    } catch (error) {
      console.log("Error fetching organization stops:", error);
      setStops([]);
    }
  };

  const handleAssignmentTypeSelect = (event) => {
    const assignmentType = event.target.value;
    if (assignmentType === "OTHER") return;
    handleInputChange({ target: { value: assignmentType } }, "assignmentType");
    clearVehicleFields();
    clearStopFields();
  };

  const handleVehicleSelect = (event) => {
    const selectedVehicleName = event.target.value;
    const selectedVehicle = siteFilteredVehicles.find(
      (vehicle) => vehicle.name === selectedVehicleName,
    );

    updateVehicleFields(selectedVehicle);
    clearStopFields();

    if (selectedVehicle) {
      if (selectedVehicle.organization) {
        handleInputChange(
          { target: { value: selectedVehicle.organization.name } },
          "organizationName",
        );

        handleInputChange(
          { target: { value: selectedVehicle.organization.city } },
          "city",
        );
      }
    } else {
      clearVehicleFields();
    }
  };

  const handleStopSelect = (event) => {
    const selectedStopId = event.target.value;
    const selectedStop = siteFilteredStops.find(
      (stop) => String(stop.id) === String(selectedStopId),
    );
    const stopData = selectedStop
      ? {
          id: selectedStop.id,
          stopId: selectedStop.stopId,
          name: selectedStop.name,
          latitude: selectedStop.latitude,
          longitude: selectedStop.longitude,
          landmark: selectedStop.landmark,
        }
      : null;

    handleInputChange({ target: { value: stopData } }, "stop");
    handleInputChange(
      { target: { value: selectedStop?.stopId || "" } },
      "stopId",
    );
    handleInputChange(
      { target: { value: selectedStop?.name || "" } },
      "stopName",
    );
    clearVehicleFields();
  };

  const handleSingleSite = (sites) => {
    if (sites.length === 1) {
      handleInputChange({ target: { value: getSiteOptionValue(sites[0]) } }, "site");
      if (!isEditMode) {
        if (!shouldKeepEditVehicle()) {
          clearVehicleFields();
        }
        clearStopFields();
      }
    }
  };

  const handleSiteSelect = (event) => {
    handleInputChange(event, "site");
    if (!isEditMode) {
      clearVehicleFields();
      clearStopFields();
    }
  };

  const autoSelectSiteFromFormData = (siteData) => {
    if (!isEditMode || !formData.site) {
      return;
    }
    const matchingSite = siteData.find(
      (site) =>
        String(site.siteId) === String(formData.site) ||
        String(site.id) === String(formData.site),
    );
    if (matchingSite) {
      handleInputChange({ target: { value: getSiteOptionValue(matchingSite) } }, "site");
    }
  };

  const fetchSites = async (organizationId) => {
    try {
      const response = await axios.get(
        `${config.baseURL}/site/organization?organizationId=${organizationId}`,
        { withCredentials: true },
      );
      const siteData = Array.isArray(response.data) ? response.data : [];
      setSites(siteData);
      if (siteData.length === 1) {
        handleSingleSite(siteData);
      } else {
        autoSelectSiteFromFormData(siteData);
      }
    } catch (error) {
      console.error("Error fetching sites:", error);
      setSites([]);
    }
  };

  const fetchOrganization = async () => {
    try {
      const response = await axios.get(`${config.baseURL}/organization`);
      setOrganizations(response?.data?.organizations || []);
    } catch (error) {
      console.log("Error fetching organizations:", error);
      setOrganizations([]);
    }
  };

  const handleOrganizationSelect = (event) => {
    if (lockOrganization) return;

    const orgName = event.target.value;
    const selectedOrg = organizations.find((org) => org.name === orgName);

    setSelectedOrganization(selectedOrg);
    handleInputChange({ target: { value: orgName } }, "organizationName");
    handleInputChange(
      { target: { value: selectedOrg?.id || "" } },
      "organizationId",
    );
    clearOrganizationDependentFields();

    if (selectedOrg?.cities?.length) {
      setCities(selectedOrg.cities);
      if (selectedOrg.cities.length === 1) {
        handleInputChange({ target: { value: selectedOrg.cities[0] } }, "city");
      }
    } else if (selectedOrg?.city) {
      setCities([selectedOrg.city]);
      handleInputChange({ target: { value: selectedOrg.city } }, "city");
    } else {
      setCities([]);
    }
  };

  const inputStyles = {
    "& .MuiOutlinedInput-root": {
      height: "55px",
      borderRadius: "16px",
      padding: "16px 24px",
      border: "2px solid #183059",
      "&:hover": {
        border: "2px solid #183059",
      },
      "&.Mui-focused": {
        border: "2px solid #183059",
      },
      "& fieldset": {
        border: "none !important",
      },
    },
    "& .MuiInputBase-input": {
      padding: 0,
    },
  };

  const selectStyles = {
    height: "55px",
    borderRadius: "16px",
    border: "2px solid #183059",
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "& .MuiSelect-select": {
      padding: "16px 24px",
      paddingRight: "48px !important",
      display: "flex",
      alignItems: "center",
      height: "23px",
    },
  };

  const displayBoxStyles = {
    height: "55px",
    borderRadius: "16px",
    backgroundColor: "#E8EAEE",
    padding: "16px 24px",
    display: "flex",
    alignItems: "center",
    minHeight: "55px",
    boxSizing: "border-box",
    fontSize: "16px",
    fontFamily: "inherit",
  };

  const radioStyles = {
    color: "#183059",
    "&.Mui-checked": { color: "#183059" },
  };

  useEffect(() => {
    if (isEditMode && formData.vehicle?.id) return; // ✅ don't overwrite in edit mode
    if (
      (formData.assignmentType || "VEHICLE") === "VEHICLE" &&
      siteFilteredVehicles.length === 1
    ) {
      const singleVehicle = siteFilteredVehicles[0];
      if (String(formData.vehicle?.id || "") === String(singleVehicle.id))
        return;
      updateVehicleFields(singleVehicle);
    }
  }, [
    formData.assignmentType,
    formData.site,
    siteFilteredVehicles,
    formData.vehicle?.id,
  ]);

  useEffect(() => {
    if (formData.organizationName && organizations.length > 0) {
      const org = organizations.find(
        (o) => o.name === formData.organizationName,
      );
      if (
        org &&
        (!selectedOrganization || org.id !== selectedOrganization.id)
      ) {
        setSelectedOrganization(org);
        handleInputChange({ target: { value: org.id } }, "organizationId");
      }
    }
  }, [formData.organizationName, organizations]);

  useEffect(() => {
    if (isEditMode) return; // ✅ don't overwrite in edit mode at all
    if (formData.vehicle && vehicles.length > 0) {
      const v = vehicles.find(
        (v) => String(v.id) === String(formData.vehicle.id),
      );
      if (v && (!formData.vehicle.name || v.name !== formData.vehicle.name)) {
        updateVehicleFields(v);
      }
    }
  }, [vehicles, formData.vehicle]);

  useEffect(() => {
    if (
      (formData.assignmentType || "VEHICLE") === "VEHICLE" &&
      siteFilteredVehicles.length === 1
    ) {
      const singleVehicle = siteFilteredVehicles[0];
      if (String(formData.vehicle?.id || "") === String(singleVehicle.id)) {
        return;
      }
      if (isEditMode && formData.vehicle?.id) {
        return;
      }
      updateVehicleFields(singleVehicle);
    }
    // eslint-disable-next-line
  }, [
    formData.assignmentType,
    formData.site,
    siteFilteredVehicles,
    formData.vehicle?.id,
  ]);

  // ================== GOOGLE MAP LOGIC ==================
  // Mounted On/Attached To "Other" is disabled for create/update.
  const assignmentType =
    formData.assignmentType === "OTHER"
      ? "VEHICLE"
      : formData.assignmentType || "VEHICLE";
  const showMap = assignmentType === "STOP";

  const mapCenter = useMemo(() => {
    if (formData.stop?.latitude && formData.stop?.longitude) {
      return {
        lat: Number(formData.stop.latitude),
        lng: Number(formData.stop.longitude),
      };
    }
    return DEFAULT_MAP_CENTER;
  }, [formData.stop?.latitude, formData.stop?.longitude]);

  const markerPosition = useMemo(() => {
    if (formData.stop?.latitude && formData.stop?.longitude) {
      return {
        lat: Number(formData.stop.latitude),
        lng: Number(formData.stop.longitude),
      };
    }
    return null;
  }, [formData.stop?.latitude, formData.stop?.longitude]);

  const onMapLoad = useCallback(
    (mapInstance) => {
      mapRef.current = mapInstance;
      // Recenter once tiles are ready (avoids gray-map issue)
      window.google.maps.event.addListenerOnce(mapInstance, "idle", () => {
        mapInstance.panTo(mapCenter);
      });
    },
    [mapCenter],
  );

  const onMapUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  // Recenter map whenever a Stop is picked from the dropdown
  useEffect(() => {
    if (
      assignmentType === "STOP" &&
      mapRef.current &&
      formData.stop?.latitude &&
      formData.stop?.longitude
    ) {
      mapRef.current.panTo({
        lat: Number(formData.stop.latitude),
        lng: Number(formData.stop.longitude),
      });
      mapRef.current.setZoom(14);
    }
  }, [assignmentType, formData.stop?.latitude, formData.stop?.longitude]);

  // Custom map location selection for Mounted On/Attached To "Other" disabled.
  // const setCustomLocation = (lat, lng) => {
  //   handleInputChange(
  //     {
  //       target: {
  //         value: {
  //           id: null,
  //           stopId: "",
  //           name: "Custom Location",
  //           latitude: lat,
  //           longitude: lng,
  //           landmark: "",
  //         },
  //       },
  //     },
  //     "stop",
  //   );
  //   handleInputChange({ target: { value: "" } }, "stopId");
  //   handleInputChange({ target: { value: "Custom Location" } }, "stopName");
  // };

  // const handleMapClick = useCallback(
  //   (e) => {
  //     if (assignmentType !== "OTHER") return;
  //     const lat = e.latLng.lat();
  //     const lng = e.latLng.lng();
  //     setCustomLocation(lat, lng);
  //   },
  //   [assignmentType],
  // );

  // const handleMarkerDragEnd = useCallback(
  //   (e) => {
  //     if (assignmentType !== "OTHER") return;
  //     const lat = e.latLng.lat();
  //     const lng = e.latLng.lng();
  //     setCustomLocation(lat, lng);
  //   },
  //   [assignmentType],
  // );
  // ================== END GOOGLE MAP LOGIC ==================

  return (
    <Box
      sx={{
        padding: { xs: "20px", md: "40px" },
       margin: { xs: '0px 20px', md: '0px 40px' },
                backgroundColor: isEditMode ? "#E8EAEE" : "#FFFFFF",
                // border: '1px solid #A5DCF3',
                borderRadius: '10px',
                // boxShadow: '-2px 2px 5px 0px #00000040',
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Paper
          sx={{
            height: 52,
            width: "100%",
            borderRadius: "8px",
            padding: "12px 24px",
            backgroundColor: "#647490",
            boxShadow: "-2px 2px 5px 0px #00000040",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h6"
            color="white"
            sx={{ fontSize: { xs: "18px", md: "20px" } }}
          >
            {t("Installation Details")}
          </Typography>
        </Paper>
      </Box>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={3} mb={1}>
                <StandardLabel label={t("Date")} />
                <TextField
                  fullWidth
                  type="date"
                  variant="outlined"
                  InputLabelProps={{ shrink: true }}
                  value={formData.installationDate}
                  onChange={(e) => handleInputChange(e, "installationDate")}
                  disabled={disabled}
                  error={!!errors.installationDate}
                  helperText={errors.installationDate}
                  sx={inputStyles}
                />
              </Grid>
              <Grid item xs={12} sm={3} mb={1.5}>
                <StandardLabel label={t("Organization Name")} />
                <Select
                  fullWidth
                  variant="outlined"
                  displayEmpty
                  value={formData.organizationName || ""}
                  onChange={handleOrganizationSelect}
                  disabled={disabled || lockOrganization}
                  sx={selectStyles}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <span style={{ color: "#999" }}>
                          {t("Select Organization")}
                        </span>
                      );
                    }
                    return selected;
                  }}
                >
                  <MenuItem value="" disabled>
                    {t("Select Organization")}
                  </MenuItem>
                  {organizations?.map((org) => (
                    <MenuItem key={org.id} value={org.name}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12} sm={3} mb={1.5}>
                <StandardLabel label={t("City")} />
                {cities.length === 1 ? (
                  <Box sx={{ ...displayBoxStyles, color: "#000" }}>
                    {cities[0]}
                  </Box>
                ) : (
                  <Select
                    fullWidth
                    variant="outlined"
                    displayEmpty
                    value={formData.city || ""}
                    onChange={(e) => handleInputChange(e, "city")}
                    sx={selectStyles}
                    disabled={disabled || !selectedOrganization}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <span style={{ color: "#999" }}>
                            {!selectedOrganization
                              ? t("Select Organization First")
                              : t("Select City")}
                          </span>
                        );
                      }
                      return selected;
                    }}
                  >
                    <MenuItem value="" disabled>
                      {!selectedOrganization
                        ? t("Select Organization First")
                        : t("Select City")}
                    </MenuItem>
                    {cities.map((city) => (
                      <MenuItem key={city} value={city}>
                        {city}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </Grid>
              <Grid item xs={12} sm={3} mb={1}>
                <StandardLabel label={t("Site")} />
                {sites.length === 1 ? (
                  <Box sx={{ ...displayBoxStyles, color: "#000" }}>
                    {getSiteDisplayName(selectedSite || sites[0])}
                  </Box>
                ) : (
                  <Select
                    fullWidth
                    variant="outlined"
                    displayEmpty
                    value={formData.site || ""}
                    onChange={handleSiteSelect}
                    sx={selectStyles}
                    disabled={disabled || !selectedOrganization || sites.length === 0}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <span style={{ color: "#999" }}>
                            {!selectedOrganization
                              ? t("Select Organization First")
                              : t("Select Site")}
                          </span>
                        );
                      }
                      const selectedSite = sites.find(
                        (site) =>
                          String(site.siteId) === String(selected) ||
                          String(site.id) === String(selected),
                      );
                      return selectedSite
                        ? getSiteDisplayName(selectedSite)
                        : selected;
                    }}
                  >
                    <MenuItem value="" disabled>
                      {!selectedOrganization
                        ? t("Select Organization First")
                        : t("Select Site")}
                    </MenuItem>
                    {sites?.map((site) => (
                      <MenuItem key={site.id} value={getSiteOptionValue(site)}>
                        {getSiteDisplayName(site)}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              </Grid>
            </Grid>

            {/* ===== Mounted On / Attached To + conditional fields + map ===== */}
            {assignmentType === "VEHICLE" ? (
              // VEHICLE mode: original flat single-row layout (no map)
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3} mb={1.5}>
                  <StandardLabel label={t("Mounted On/Attached To")} />
                  <RadioGroup
                    row
                    value={assignmentType}
                    onChange={handleAssignmentTypeSelect}
                    sx={{
                      height: "55px",
                      alignItems: "center",
                      "& .MuiFormControlLabel-label": {
                        fontFamily: "Roboto",
                        fontSize: "14px",
                        color: "#1F242E",
                      },
                    }}
                  >
                    <FormControlLabel
                      value="VEHICLE"
                      disabled={disabled}
                      control={<Radio sx={radioStyles} />}
                      label={t("Vehicle")}
                    />
                    <FormControlLabel
                      value="STOP"
                      disabled={disabled}
                      control={<Radio sx={radioStyles} />}
                      label={t("Stop")}
                    />
                    {/*
                    Mounted On/Attached To "Other" option disabled for create/update.
                    <FormControlLabel
                      value="OTHER"
                      control={<Radio sx={radioStyles} />}
                      label={t("Other")}
                    />
                    */}
                  </RadioGroup>
                </Grid>

                <Grid item xs={12} sm={3} mb={1.5}>
                  <StandardLabel label={t("Vehicle Name")} />
                  {siteFilteredVehicles.length === 1 ? (
                    <Box sx={{ ...displayBoxStyles, color: "#000" }}>
                      {siteFilteredVehicles[0].name}
                    </Box>
                  ) : (
                    <Select
                      fullWidth
                      variant="outlined"
                      displayEmpty
                      value={selectedVehicleName}
                      onChange={handleVehicleSelect}
                      sx={selectStyles}
                      disabled={
                        disabled ||
                        !selectedOrganization ||
                        siteFilteredVehicles.length === 0
                      }
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <span style={{ color: "#999" }}>
                              {!selectedOrganization
                                ? t("Select Organization First")
                                : siteFilteredVehicles.length === 0
                                  ? t("No Vehicles Found")
                                  : t("Select Vehicle")}
                            </span>
                          );
                        }
                        return selected;
                      }}
                    >
                      <MenuItem value="" disabled>
                        {!selectedOrganization
                          ? t("Select Organization First")
                          : siteFilteredVehicles.length === 0
                            ? t("No Vehicles Found")
                            : t("Select Vehicle")}
                      </MenuItem>
                      {siteFilteredVehicles?.map((vehicle) => (
                        <MenuItem key={vehicle.id} value={vehicle.name}>
                          {vehicle.name}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                </Grid>
                <Grid item xs={12} sm={3} mb={1.5}>
                  <StandardLabel label={t("Vehicle Registration Number")} />
                  <Box
                    sx={{
                      ...displayBoxStyles,
                      color: formData.vehicleRegistrationNumber
                        ? "#000"
                        : "#95A0B3",
                    }}
                  >
                    {formData.vehicleRegistrationNumber || "e.g WB26AG1234"}
                  </Box>
                </Grid>
                <Grid item xs={12} sm={3} mb={1.5}>
                  <StandardLabel label={t("Vehicle Model")} />
                  <Box
                    sx={{
                      ...displayBoxStyles,
                      color: formData.vehicleModel ? "#000" : "#95A0B3",
                    }}
                  >
                    {formData.vehicleModel || "e.g Tata Ace Gold"}
                  </Box>
                </Grid>

                <Grid item xs={12} mb={1.5}>
                  <StandardLabel label={t("Remarks")} />
                  <TextField
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    placeholder={t("typeRemarks")}
                    value={formData.remarks || ""}
                    onChange={(e) => handleInputChange(e, "remarks")}
                    disabled={disabled}
                    multiline
                    rows={4}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "16px",
                        padding: "16px 24px",
                        border: errors.remarks
                          ? "2px solid #d32f2f"
                          : "2px solid #183059",
                        "&:hover": {
                          border: errors.remarks
                            ? "2px solid #d32f2f"
                            : "2px solid #183059",
                        },
                        "&.Mui-focused": {
                          border: errors.remarks
                            ? "2px solid #d32f2f"
                            : "2px solid #183059",
                        },
                        "& fieldset": {
                          border: "none !important",
                        },
                      },
                      "& .MuiInputBase-input": {
                        padding: 0,
                      },
                    }}
                    error={!!errors.remarks}
                    helperText={errors.remarks}
                  />
                </Grid>
              </Grid>
            ) : (
              // STOP mode: two-column layout with map on the right
              <Grid container spacing={2}>
                {/* LEFT COLUMN: radio + conditional fields stack vertically */}
                <Grid item xs={12} sm={6}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} mb={1.5}>
                      <StandardLabel label={t("Mounted On/Attached To")} />
                      <RadioGroup
                        row
                        value={assignmentType}
                        onChange={handleAssignmentTypeSelect}
                        sx={{
                          height: "55px",
                          alignItems: "center",
                          "& .MuiFormControlLabel-label": {
                            fontFamily: "Roboto",
                            fontSize: "14px",
                            color: "#1F242E",
                          },
                        }}
                      >
                        <FormControlLabel
                          value="VEHICLE"
                          disabled={disabled}
                          control={<Radio sx={radioStyles} />}
                          label={t("Vehicle")}
                        />
                        <FormControlLabel
                          value="STOP"
                          disabled={disabled}
                          control={<Radio sx={radioStyles} />}
                          label={t("Stop")}
                        />
                        {/*
                        Mounted On/Attached To "Other" option disabled for create/update.
                        <FormControlLabel
                          value="OTHER"
                          control={<Radio sx={radioStyles} />}
                          label={t("Other")}
                        />
                        */}
                      </RadioGroup>
                    </Grid>

                    {assignmentType === "STOP" && (
                      <Grid item xs={12} sm={6} mb={1.5}>
                        <StandardLabel label={t("Stop Name")} />
                        <Select
                          fullWidth
                          variant="outlined"
                          displayEmpty
                          value={formData.stop?.id || ""}
                          onChange={handleStopSelect}
                          sx={selectStyles}
                          disabled={
                            disabled ||
                            !selectedOrganization ||
                            siteFilteredStops.length === 0
                          }
                          renderValue={(selected) => {
                            if (!selected) {
                              return (
                                <span style={{ color: "#999" }}>
                                  {!selectedOrganization
                                    ? t("Select Organization First")
                                    : siteFilteredStops.length === 0
                                      ? t("No Stops Found")
                                      : t("Select Stop")}
                                </span>
                              );
                            }
                            const selectedStop = siteFilteredStops.find(
                              (stop) => String(stop.id) === String(selected),
                            );
                            return selectedStop ? selectedStop.name : selected;
                          }}
                        >
                          <MenuItem value="" disabled>
                            {!selectedOrganization
                              ? t("Select Organization First")
                              : siteFilteredStops.length === 0
                                ? t("No Stops Found")
                                : t("Select Stop")}
                          </MenuItem>
                          {siteFilteredStops.map((stop) => (
                            <MenuItem key={stop.id} value={stop.id}>
                              {stop.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </Grid>
                    )}

                    {/*
                    Custom map location for Mounted On/Attached To "Other" disabled.
                    {assignmentType === "OTHER" &&
                      formData.stop?.latitude &&
                      formData.stop?.longitude && (
                        <Grid item xs={12} sm={6} mb={1.5}>
                          <StandardLabel label={t("Selected Location")} />
                          <Box
                            sx={{
                              ...displayBoxStyles,
                              color: "#000",
                              fontSize: "13px",
                            }}
                          >
                            {Number(formData.stop.latitude).toFixed(6)},{" "}
                            {Number(formData.stop.longitude).toFixed(6)}
                          </Box>
                        </Grid>
                      )}
                    */}

                    {/* Remarks sits under the fields, same column, matching reference layout */}
                    <Grid item xs={12} mb={1.5}>
                      <StandardLabel label={t("Remarks")} />
                      <TextField
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{ shrink: false }}
                        placeholder={t("typeRemarks")}
                        value={formData.remarks || ""}
                        onChange={(e) => handleInputChange(e, "remarks")}
                        disabled={disabled}
                        multiline
                        rows={4}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "16px",
                            padding: "16px 24px",
                            border: errors.remarks
                              ? "2px solid #d32f2f"
                              : "2px solid #183059",
                            "&:hover": {
                              border: errors.remarks
                                ? "2px solid #d32f2f"
                                : "2px solid #183059",
                            },
                            "&.Mui-focused": {
                              border: errors.remarks
                                ? "2px solid #d32f2f"
                                : "2px solid #183059",
                            },
                            "& fieldset": {
                              border: "none !important",
                            },
                          },
                          "& .MuiInputBase-input": {
                            padding: 0,
                          },
                        }}
                        error={!!errors.remarks}
                        helperText={errors.remarks}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* RIGHT COLUMN: map */}
                <Grid item xs={12} sm={6}>
                  <StandardLabel
                    label={t("Map")}
                  />
                  <Box
                    sx={{
                      width: "100%",
                      height: { xs: 260, sm: 320 },
                      borderRadius: "8px",
                      overflow: "hidden",
                      border: "1px solid #183059",
                    }}
                  >
                    {isMapLoaded ? (
                      <GoogleMap
                        mapContainerStyle={mapContainerStyle}
                        center={mapCenter}
                        zoom={markerPosition ? 14 : 11}
                        onLoad={onMapLoad}
                        onUnmount={onMapUnmount}
                        onClick={undefined}
                        options={{
                          styles: mapStyles,
                          streetViewControl: false,
                          mapTypeControl: false,
                          fullscreenControl: false,
                        }}
                      >
                        {markerPosition && (
                          <>
                            {assignmentType === "STOP" && (
                              <OverlayView
                                position={markerPosition}
                                mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                              >
                                <div
                                  style={{
                                    position: "relative",
                                    top: "0px",
                                    left: "0px",
                                  }}
                                >
                                  <div
                                    style={{
                                      position: "absolute",
                                      bottom: "44px",
                                      left: "50%",
                                      transform: "translateX(-50%)",
                                      backgroundColor: "white",
                                      color: "black",
                                      padding: "4px 8px",
                                      borderRadius: "4px",
                                      fontSize: "12px",
                                      fontWeight: 500,
                                      boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                      whiteSpace: "nowrap",
                                      zIndex: 1000,
                                    }}
                                  >
                                    {formData.stop?.name ||
                                      formData.stopName ||
                                      t("Stop")}
                                  </div>
                                </div>
                              </OverlayView>
                            )}
                            <Marker
                              position={markerPosition}
                              draggable={false}
                              onDragEnd={undefined}
                            />
                          </>
                        )}
                      </GoogleMap>
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#E8EAEE",
                          color: "#647490",
                          fontSize: "14px",
                        }}
                      >
                        {t("Loading map...")}
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            )}
            {/* ===== End Mounted On / Attached To ===== */}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
